import { InfoIcon } from "lucide-react";
import { Tooltip } from "@component/tooltip";
import type { RubricScores } from "@type/training";
import { cn } from "@util/tailwind";

export const RUBRIC_DESCRIPTIONS: Record<keyof RubricScores, string> = {
    empathy:
        "Did you acknowledge how the customer felt and respond with care before solving?",
    problem_resolution:
        "Did you understand the issue, propose a clear path, and confirm it solved the problem?",
    product_knowledge:
        "Did you describe products, policies, and tradeoffs accurately and confidently?",
    tone: "Was your delivery warm, calm, and professional throughout the call?",
    call_handling:
        "Did you control pacing, set expectations, and close the call cleanly?",
};

function scoreColor(value: number): string {
    if (value >= 4) return "bg-emerald-500";
    if (value >= 3) return "bg-blue-500";
    if (value >= 2) return "bg-amber-500";
    return "bg-rose-500";
}

// Compact 1–5 score bar: label left, short fixed-width bar + score right.
// `trailing` renders just to the right of the label text (e.g. a Highest/Lowest badge).
export function RubricBar({
    label,
    value,
    description,
    trailing,
}: {
    label: string;
    value: number;
    description?: string;
    trailing?: React.ReactNode;
}) {
    const pct = (value / 5) * 100;
    return (
        <div className="flex items-center gap-3 py-1.5">
            <span className="text-sm text-gray-700 flex-1 min-w-0 flex items-center gap-1.5">
                <span className="truncate">{label}</span>
                {trailing}
                {description && (
                    <Tooltip
                        content={
                            <div className="max-w-xs text-xs leading-relaxed">
                                <div className="font-semibold mb-1">Why this matters</div>
                                {description}
                            </div>
                        }
                    >
                        <button
                            type="button"
                            aria-label={`About ${label}`}
                            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                        >
                            <InfoIcon className="w-3 h-3" />
                        </button>
                    </Tooltip>
                )}
            </span>
            <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden flex-shrink-0">
                <div
                    className={cn("h-full transition-all duration-500", scoreColor(value))}
                    style={{ width: `${pct}%` }}
                />
            </div>
            <span className="text-xs font-semibold text-gray-900 tabular-nums w-8 text-right flex-shrink-0">
                {value}<span className="text-gray-400 font-normal">/5</span>
            </span>
        </div>
    );
}

// Total score (out of 100) → semantic band used in headers and badges.
export function rubricGradeBand(total: number): { label: string; cls: string } {
    if (total >= 84)
        return { label: "Excellent", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" };
    if (total >= 68)
        return { label: "Strong", cls: "text-blue-700 bg-blue-50 border-blue-200" };
    if (total >= 52)
        return { label: "Developing", cls: "text-amber-700 bg-amber-50 border-amber-200" };
    return { label: "Needs work", cls: "text-rose-700 bg-rose-50 border-rose-200" };
}
